<script lang="ts">
import { helpers } from '@vuelidate/validators';

const validDomain = (url: string) => {
  return !helpers.req(url) || /^[a-zA-Z0-9][a-zA-Z0-9-]{0,61}[a-zA-Z0-9](?:\.[a-zA-Z]{2,}){1}$/.test(url);
};

const noWWW = (url: string) => {
  return !helpers.req(url) || !/^www\..*$/.test(url);
};

export default {
  name: 'AccountForm',
  validations: {
    form: {
      stockTicker: {
        maxLength: maxLength(10),
      },
      domainName: {
        maxLength: maxLength(100),
        validDomain,
        noWWW,
      },
      displayName: {
        maxLength: maxLength(100),
      },
    },
  },
};
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted, inject } from 'vue';
import { accountService, referenceService } from '@/services/api';
import { accountService as accountServiceV2 } from '@/services/v2/AccountService';
import { maxLength } from '@vuelidate/validators';
import { FailedToSaveAccount, InvalidDomainName, RemoveWWW, TooLong } from '@/utilities/Errors';
import { AppState } from '@/store';
import { IsUserAnAdmin, tenantName } from '@/utilities/AuthUtils';
import { Account, PublicCompanySearchResult } from '@/types/Account';
import { ClientIndustry } from '@/types/Industry';
import { Industry } from '@/types/Industry';
import { StockExchange } from '@/types/StockExchange';
import { AccountSummary } from '@/types/AccountSummary';
import { Store } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { onBeforeRouteLeave, useRoute, useRouter } from 'vue-router';
import { AxiosError } from 'axios';
import { Info, CircleCheck, TriangleAlert } from 'lucide-vue-next';
import AppModal from '@/components/v2/AppModal.vue';
import SICCodesAutocompleteInput from '@/components/SICCodesAutocompleteInput.vue';
import AppButton from '@/components/v2/AppButton.vue';
import ConfirmModal from '@/components/v2/ConfirmModal.vue';
import TextField from '@/components/v2/TextField.vue';
import Combobox from '@/components/v2/Combobox.vue';
import Checkbox from '@/components/v2/Checkbox.vue';
import Spinner from '@/components/v2/Spinner.vue';
import { useToast } from '@/composables/useToast';

interface Form {
  capIQId: number | null;
  sic: string;
  stockTicker: string;
  stockExchange: string | null;
  ownershipType: number | null;
  parentAccountID: number | null;
  domainName: string;
  domainDoesNotExist: boolean;
  noParentAccount: boolean;
  accountAttributes: number[];
  industry: number | null;
  displayName: string;
  accountActive: boolean;
  inactiveReason: string | null;
}

const store = inject<Store<AppState>>('store');
const $route = useRoute();
const $router = useRouter();
const toast = useToast();

// -----
// data
// -----
const loading = ref<boolean>(true);
const saving = ref<boolean>(false);
const saveErrorMessage = ref<string>('');
const saveWarningMessage = ref<string>('');
const account = ref<Account>({} as Account);
const allSics = ref<ClientIndustry[]>([]);
const allIndustries = ref<Industry[]>([]);
const stockExchanges = ref<StockExchange[]>([]);
const allCompanies = ref<AccountSummary[]>([]);
const accountAttributes = ref<Record<string, string>[]>([]);
const showCompanyPickerModal = ref<boolean>(false);
const companySearchLoading = ref<boolean>(false);
const companySearchResults = ref<PublicCompanySearchResult[]>([]);
const companySearchSelection = ref<number | undefined>(undefined);
const initialFormValue = ref<Form>();
const checkFormDirty = ref<boolean>(true);
const confirmModal = ref<typeof ConfirmModal>();

const form = ref<Form>({
  capIQId: null,
  sic: '',
  stockTicker: '',
  stockExchange: null,
  ownershipType: null,
  parentAccountID: null,
  domainName: '',
  domainDoesNotExist: false,
  noParentAccount: false,
  accountAttributes: [],
  industry: null,
  displayName: '',
  accountActive: true,
  inactiveReason: null,
});

const accountsCountByEpicLookupCode = ref<number>(0);

// ------------
// validation
// ------------
const validateRules = {
  form: {
    stockTicker: {
      maxLength: maxLength(10),
    },
    domainName: {
      maxLength: maxLength(100),
      validDomain,
      noWWW,
    },
    displayName: {
      maxLength: maxLength(100),
    },
  },
};

const v$ = useVuelidate<Form>(validateRules, form);

// ---------
// computed
// ---------
const isAdmin = computed(() => {
  return IsUserAnAdmin(store?.state.user);
});

const showSaveWarningMessage = computed({
  get: () => {
    return !!saveWarningMessage.value;
  },
  set: (newValue) => {
    if (newValue === false) {
      saveWarningMessage.value = '';
    }
  },
});

const stockTickerErrors = computed(() => {
  const errors: string[] = [];
  if (!v$.value.form.stockTicker.$dirty) return errors;
  if (!v$.value.form.stockTicker.maxLength) errors.push(TooLong(10));
  return errors;
});

const domainNameErrors = computed(() => {
  const errors: string[] = [];
  if (!v$.value.form.domainName.$dirty) return errors;
  if (!v$.value.form.domainName.maxLength) errors.push(TooLong(100));
  if (!v$.value.form.domainName.noWWW) errors.push(RemoveWWW);
  if (!v$.value.form.domainName.validDomain) errors.push(InvalidDomainName);
  return errors;
});

const displayNameErrors = computed(() => {
  const errors: string[] = [];
  if (!v$.value.form.displayName.$dirty) return errors;
  if (!v$.value.form.displayName.maxLength) errors.push(TooLong(100));
  return errors;
});

const industries = computed(() => {
  return allIndustries.value;
});

const companies = computed(() => {
  return allCompanies.value.filter((company) => company.accountID !== Number(account.value.accountID) && !!company.accountID);
});

const industryOptions = computed(() => industries.value.map((i) => ({ label: i.name, value: String(i.ID) })));
const industryModel = computed({
  get: () => (form.value.industry != null ? String(form.value.industry) : ''),
  set: (v) => {
    form.value.industry = v === '' ? null : Number(v);
  },
});

const stockExchangeOptions = computed(() => stockExchanges.value.map((e) => ({ label: `${e.Abbrev} - ${e.Name}`, value: e.Abbrev })));
const stockExchangeModel = computed({
  get: () => form.value.stockExchange ?? '',
  set: (v) => {
    form.value.stockExchange = v === '' ? null : v;
  },
});

const companiesOptions = computed(() => companies.value.map((c) => ({ label: c.accountName, value: String(c.accountID) })));
const parentAccountModel = computed({
  get: () => (form.value.parentAccountID != null ? String(form.value.parentAccountID) : ''),
  set: (v) => {
    form.value.parentAccountID = v === '' ? null : Number(v);
  },
});

const inactiveReasonModel = computed({
  get: () => form.value.inactiveReason ?? '',
  set: (v) => {
    form.value.inactiveReason = v === '' ? null : (v as string);
  },
});

const isPublic = computed(() => {
  return form.value.ownershipType === 1;
});

const isSaveButtonEnabled = computed(() => {
  return !saving.value && !v$.value.$invalid && isInactiveReasonValid(form.value.inactiveReason) === true;
});

const companyValidationModalTitle = computed(() => {
  if (companySearchLoading.value) {
    return 'Searching...';
  }

  if (form.value.stockExchange && form.value.stockTicker) {
    return `Results for ${form.value.stockExchange.toUpperCase()}: ${form.value.stockTicker.toUpperCase()}`;
  }

  return 'Results';
});

const isFormDirty = computed(() => {
  return JSON.stringify(initialFormValue.value) !== JSON.stringify(form.value);
});

// ---------
// watchers
// ---------
watch(isPublic, (value) => {
  if (!value) {
    form.value.stockTicker = '';
    form.value.stockExchange = null;
  }
});

watch(
  () => form.value.noParentAccount,
  (noParent) => {
    if (noParent) {
      form.value.parentAccountID = null;
    } else {
      form.value.parentAccountID = account.value.parentAccountID || null;
      if (form.value.parentAccountID === Number(account.value.accountID)) {
        form.value.parentAccountID = null;
      }
    }
  },
);

watch(
  () => form.value.stockExchange,
  (stockExchange) => {
    if (stockExchange !== account.value.stockExchange) {
      form.value.capIQId = null;
    } else {
      form.value.capIQId = account.value.capIQId;
    }
  },
);

watch(
  () => form.value.stockTicker,
  (stockTicker) => {
    if (stockTicker !== account.value.stockTicker) {
      form.value.capIQId = null;
    } else {
      form.value.capIQId = account.value.capIQId;
    }
  },
);

// -----------
// lifecycles
// -----------
onMounted(async () => {
  try {
    const [sicCodes, stockExchangesResult, companies, accountResult, attributes, attributeTypes, industries] = await Promise.all([
      referenceService.sicCodes(),
      referenceService.stockExchanges(),
      accountService.list(),
      accountService.findById($route.params.id as string),
      accountService.attributes($route.params.id as string),
      referenceService.accountAttributeTypes(),
      referenceService.industries(),
    ]);

    allSics.value = sicCodes;
    allIndustries.value = industries;
    stockExchanges.value = stockExchangesResult;
    allCompanies.value = companies;

    // only show attributes that match to account's ownership type; if unknown (null) display all
    accountAttributes.value = attributeTypes
      .filter((t) => (accountResult.ownershipType ? accountResult.ownershipType === t.ownershipType || t.ownershipType === 'Both' : true))
      .map((t) => ({ title: t.name, value: t.id }));
    form.value.accountAttributes = accountAttributes.value.filter((t) => attributes.find((a) => a === Number(t.value))).map((t) => Number(t.value));

    accountAttributes.value = attributeTypes
      .filter((t) => (accountResult.ownershipType ? accountResult.ownershipType === t.ownershipType || t.ownershipType === 'Both' : true))
      .map((t) => ({ title: t.name, value: t.id }));
    account.value = accountResult;

    const accountId = Number(accountResult.accountID);

    form.value.displayName = accountResult.displayName;
    const sicCode = sicCodes.find((i) => i.sicCode === accountResult.sIC);
    if (sicCode) {
      form.value.sic = sicCode.sicCode;
    }
    form.value.industry = industries.find((i) => i.ID === accountResult.industryCode)?.ID ?? null;
    form.value.stockTicker = accountResult.stockTicker;
    form.value.stockExchange = stockExchangesResult.find((e) => e.Abbrev.trim() === accountResult.stockExchange?.trim())?.Abbrev ?? null;
    form.value.ownershipType = { Private: 0, Public: 1, null: 2 }[accountResult.ownershipType] ?? null;
    form.value.noParentAccount = accountResult.noParent;
    form.value.parentAccountID = companies.find((c) => c.accountID === accountResult.parentAccountID && c.accountID !== accountId)?.accountID ?? null;
    form.value.domainName = accountResult.domainName;
    form.value.domainDoesNotExist = accountResult.noDomain;
    form.value.accountActive = !accountResult.inactiveDate;
    form.value.inactiveReason = accountResult.inactiveReason || null;
    form.value.capIQId = accountResult.capIQId;

    initialFormValue.value = { ...form.value };

    if (accountResult.epicLookupCode) {
      accountsCountByEpicLookupCode.value = await accountServiceV2.getAccountsCountByEpicLookupCode(accountResult.epicLookupCode);
    }
  } catch (err) {
    console.error(err);
    return $router.push('/accounts');
  } finally {
    loading.value = false;
  }
});

onBeforeRouteLeave(async (to, from, next) => {
  if (checkFormDirty.value && isFormDirty.value) {
    const confirmed = await confirmModal.value?.showConfirm('You have unsaved changes. Are you sure you want to leave?');
    if (confirmed) {
      next();
    } else {
      next(false);
    }
  } else {
    next();
  }
});

// ----------
// functions
// ----------
function cancel() {
  goBack();
}

function toggleAccountAttribute(id: number) {
  const idx = form.value.accountAttributes.indexOf(id);
  if (idx >= 0) {
    form.value.accountAttributes = form.value.accountAttributes.filter((x) => x !== id);
  } else {
    form.value.accountAttributes = [...form.value.accountAttributes, id];
  }
}

function goBack() {
  if (window.history.length > 1) {
    return $router.back();
  } else {
    return $router.push({ path: '/accounts' });
  }
}

async function submit() {
  try {
    saving.value = true;
    v$.value.$touch();
    if (!v$.value.$invalid && isInactiveReasonValid(form.value.inactiveReason) === true) {
      const request = {
        stockTicker: form.value.stockTicker,
        stockExchange: form.value.stockExchange,
        ownershipType: ['Private', 'Public', null][form.value.ownershipType ?? 2], // 2 is unknown
        sIC: form.value.sic === 'null' ? null : form.value.sic,
        noParent: form.value.noParentAccount,
        parentAccountID: form.value.parentAccountID,
        domainName: form.value.domainName,
        noDomain: form.value.domainDoesNotExist,
        attributes: form.value.accountAttributes,
        industrycode: !form.value.industry ? null : form.value.industry,
        displayName: form.value.displayName,
        active: form.value.accountActive,
        inactiveReason: form.value.inactiveReason,
        capIQId: form.value.capIQId,
      };

      // clear out the capIQId if the ownership type is set to non-public

      if (request.ownershipType !== 'Public') {
        request.capIQId = null;
      }

      const res = await accountService.update($route.params.id as string, request);

      if (res.epicFail) {
        saveWarningMessage.value = 'Account details saved, however there was an error writing back to Epic.';
      } else {
        checkFormDirty.value = false;
        goBack();
      }
    }
  } catch (err) {
    console.error(err);

    if (isAxiosError(err as AxiosError)) {
      saveErrorMessage.value = 'ERROR: ' + (err as AxiosError).response?.data;
    } else {
      saveErrorMessage.value = FailedToSaveAccount;
    }
    toast.error(saveErrorMessage.value);
  } finally {
    saving.value = false;
  }
}

function isAxiosError(err: AxiosError): err is AxiosError {
  return err.isAxiosError;
}

function isInactiveReasonValid(val: string | null) {
  if (form.value.accountActive) return true;

  if (!val || val.trim().length === 0) {
    return 'A reason is required to deactivate an account';
  }
  return true;
}

async function validateCompany() {
  if (form.value.stockTicker && form.value.stockExchange) {
    showCompanyPickerModal.value = true;
    companySearchLoading.value = true;
    companySearchResults.value = await accountService.searchForPublicCompany(form.value.stockTicker, form.value.stockExchange);
    companySearchLoading.value = false;
  }
}

function cancelCompanyValidationModal() {
  showCompanyPickerModal.value = false;
  companySearchSelection.value = undefined;
}

function confirmCompanyValidationModal() {
  if (companySearchSelection.value) {
    form.value.capIQId = companySearchSelection.value;
    showCompanyPickerModal.value = false;
  }
}
</script>

<template>
  <ConfirmModal ref="confirmModal" />

  <div class="root inner">
    <div
      v-visible="showSaveWarningMessage"
      class="tw:toast-center tw:toast-top tw:toast tw:z-2000">
      <div class="tw:alert tw:bg-warning tw:text-black">
        <TriangleAlert />
        <span>{{ saveWarningMessage }}</span>
        <app-button
          @click="
            showSaveWarningMessage = false;
            goBack();
          "
          class="tw:text-xs"
          >OK</app-button
        >
      </div>
    </div>

    <div
      class="progress text-center"
      v-if="loading">
      <Spinner
        size="lg"
        type="spinner"
        class="tw:loading-primary" />
    </div>

    <div v-if="!loading">
      <h1>Editing {{ account.accountName }}</h1>

      <form @submit.prevent="submit">
        <fieldset>
          <div>
            <h2>Display Name</h2>
            <p>
              Enter a Display Name for this client. This name will replace the Epic account name in certain visualizations for conciseness and
              clarity.
            </p>
          </div>
          <div>
            <TextField
              v-model="form.displayName"
              label="Display Name"
              :error="displayNameErrors.length > 0"
              :error-message="displayNameErrors[0]"
              data-test="displayName"
              @update:model-value="v$.form.displayName.$touch()"
              @blur="v$.form.displayName.$touch()" />
          </div>
        </fieldset>

        <fieldset>
          <div>
            <h2>SIC Industry</h2>
            <p>
              Assign an industry to the client based off of SIC Codes. These industries are used for client benchmarking as well as book of business
              visualizations.
            </p>
          </div>
          <SICCodesAutocompleteInput
            v-model="form.sic"
            class="tw:w-full"
            input-class="tw:input-md tw:border-gray-400" />
        </fieldset>

        <fieldset>
          <div>
            <h2>{{ tenantName() }} Industry</h2>
            <p>
              The {{ tenantName() }} industry is a company-defined classification of accounts and is assigned in Epic in the "Account Source" field.
              If the Account Source field is updated in Epic, the change will be reflected on the Platform the next day.
            </p>
          </div>
          <div>
            <label class="tw:label">
              <span class="tw:label-text">Industry</span>
            </label>
            <Combobox
              v-model="industryModel"
              :options="industryOptions"
              placeholder="Industry"
              data-test="industry" />

            <div
              v-if="!account.sourceAccountId"
              class="tw:flex tw:items-center tw:gap-1">
              <TriangleAlert class="tw:text-slate-light" />
              <span class="tw:text-slate-dark"> Writing back to Epic is not available for this client. </span>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div>
            <h2>Ownership Type</h2>
            <p>
              If a client is publicly traded, select "Public," enter the stock ticker symbol, and select the stock exchange on which the company is
              listed.
            </p>
            <p class="tw:mt-2">If a client is privately held, ensure "Private" is selected.</p>
          </div>
          <div>
            <div class="tw:mb-8 tw:join">
              <button
                @click.prevent="form.ownershipType = 0"
                class="tw:btn tw:join-item tw:btn-sm"
                :class="{ 'tw:bg-primary tw:text-white': form.ownershipType === 0 }">
                Private
              </button>
              <button
                @click.prevent="form.ownershipType = 1"
                class="tw:btn tw:join-item tw:btn-sm"
                :class="{ 'tw:bg-primary tw:text-white': form.ownershipType === 1 }">
                Public
              </button>
              <button
                @click.prevent="form.ownershipType = 2"
                class="tw:btn tw:join-item tw:btn-sm"
                :class="{ 'tw:bg-primary tw:text-white': form.ownershipType === 2 }">
                Unknown
              </button>
            </div>

            <div
              class="side-by-side"
              v-if="isPublic">
              <TextField
                v-model="form.stockTicker"
                label="Stock Ticker"
                placeholder="Ticker Symbol"
                :error="stockTickerErrors.length > 0"
                :error-message="stockTickerErrors[0]"
                data-test="stockTicker"
                @update:model-value="v$.form.stockTicker.$touch()"
                @blur="v$.form.stockTicker.$touch()" />
              <div>
                <label class="tw:label">
                  <span class="tw:label-text">Stock Exchange</span>
                </label>
                <Combobox
                  v-model="stockExchangeModel"
                  :options="stockExchangeOptions"
                  placeholder="Stock Exchange"
                  data-test="stockExchange" />
              </div>
              <div class="tw:mt-3 tw:flex tw:h-fit tw:items-center tw:gap-3">
                <CircleCheck
                  :class="{
                    'tw:text-gray-300': !form.capIQId || !form.stockTicker || !form.stockExchange,
                    'tw:text-company-green': form.capIQId && form.stockTicker && form.stockExchange,
                  }" />
                <button
                  @click.prevent="validateCompany"
                  class="tw:btn tw:join-item tw:btn-sm tw:btn-primary"
                  :class="{ 'tw:btn-disabled': !form.stockTicker || !form.stockExchange }">
                  Validate
                </button>
              </div>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div>
            <h2>Attributes</h2>
            <p>
              Select one or more attributes from the provided list to categorize this account. If none apply, simply leave the check boxes deselected.
              These selections will facilitate search capabilities on the Platform, such as the Client Management and Policy Explorer pages.
              Selections can be removed as needed.
            </p>
          </div>
          <div>
            <label class="tw:label tw:mb-2">
              <span class="tw:label-text">Account Attributes</span>
            </label>
            <div class="tw:flex tw:flex-wrap tw:gap-3">
              <Checkbox
                v-for="attr in accountAttributes"
                :key="attr.value"
                :model-value="form.accountAttributes.includes(Number(attr.value))"
                :label="attr.title"
                @update:model-value="toggleAccountAttribute(Number(attr.value))" />
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div>
            <h2>Parent Company</h2>
            <p>
              In certain circumstances, clients may be a subsidiary of a parent company that also has risk placed with CAC. Where applicable,
              companies can be grouped under their parent company in order to provide a portfolio view of all policies, claims, etc.
            </p>
          </div>
          <div>
            <label class="tw:label">
              <span class="tw:label-text">Parent Company</span>
            </label>
            <Combobox
              v-model="parentAccountModel"
              :options="companiesOptions"
              placeholder="Parent Company"
              :disabled="form.noParentAccount" />

            <label
              v-if="isAdmin"
              for="no-parent"
              class="tw:label tw:mt-2 tw:flex tw:items-center tw:justify-start tw:gap-2">
              <input
                type="checkbox"
                id="no-parent"
                class="tw:checkbox tw:border tw:border-solid tw:border-gray-400 tw:checkbox-sm"
                v-model="form.noParentAccount"
                :checked="form.noParentAccount" />
              <span>Client Does Not Have Parent</span>
            </label>

            <div
              v-else-if="form.noParentAccount"
              role="alert"
              class="tw:mt-4 tw:alert tw:text-xs">
              <Info :size="20" />
              <span>
                Selecting a parent account for this client has been disabled. To adjust this setting, please contact
                <a href="mailto:platform@cacspecialty.com">platform@cacspecialty.com</a>.
              </span>
            </div>
          </div>
        </fieldset>

        <fieldset>
          <div><h2>Domain Name</h2></div>
          <div>
            <TextField
              v-model="form.domainName"
              label="Domain Name"
              placeholder="example.com"
              hint="e.g. example.com"
              :error="domainNameErrors.length > 0"
              :error-message="domainNameErrors[0]"
              data-test="domain"
              :disabled="form.domainDoesNotExist"
              @update:model-value="v$.form.domainName.$touch()"
              @blur="v$.form.domainName.$touch()" />

            <label
              for="no-domain"
              class="tw:label tw:flex tw:items-center tw:justify-start tw:gap-2">
              <input
                type="checkbox"
                id="no-domain"
                class="tw:checkbox tw:border tw:border-solid tw:border-gray-400 tw:checkbox-sm"
                v-model="form.domainDoesNotExist"
                :checked="form.domainDoesNotExist" />
              <span>Domain Does Not Exist</span>
            </label>
          </div>
        </fieldset>

        <fieldset v-if="isAdmin">
          <div><h2>Account Status</h2></div>
          <div>
            <div class="tw:mb-2 tw:flex tw:gap-4">
              <label class="tw:label tw:cursor-pointer tw:gap-2">
                <input
                  type="radio"
                  class="tw:radio tw:radio-primary"
                  :checked="form.accountActive === true"
                  @change="form.accountActive = true" />
                <span class="tw:label-text">Active</span>
              </label>
              <label class="tw:label tw:cursor-pointer tw:gap-2">
                <input
                  type="radio"
                  class="tw:radio tw:radio-primary"
                  :checked="form.accountActive === false"
                  @change="form.accountActive = false" />
                <span class="tw:label-text">Inactive</span>
              </label>
            </div>

            <TextField
              v-if="!form.accountActive"
              v-model="inactiveReasonModel"
              label="Deactivation reason (required)"
              :error="form.inactiveReason !== null && form.inactiveReason !== '' && isInactiveReasonValid(form.inactiveReason) !== true"
              data-test="inactiveReason"
              :disabled="form.accountActive" />
          </div>
        </fieldset>

        <div class="buttons">
          <AppButton
            class="tw:mr-4"
            @click="cancel"
            :disabled="saving"
            data-test="cancel"
            >Cancel</AppButton
          >
          <AppButton
            primary
            :disabled="!isSaveButtonEnabled"
            :loading="saving"
            data-test="save"
            @click="submit">
            Save Account
          </AppButton>
        </div>
      </form>
    </div>

    <AppModal
      :title="companyValidationModalTitle"
      size="lg"
      actionButtonLabel="Confirm"
      :action-disabled="companySearchSelection === undefined"
      :show="showCompanyPickerModal"
      :handle-cancel="cancelCompanyValidationModal"
      :handle-action="confirmCompanyValidationModal"
      modal-class="tw:overflow-y-auto">
      <template v-slot:content>
        <div
          v-if="companySearchLoading"
          class="tw:loading tw:mx-auto tw:flex tw:loading-lg tw:justify-center tw:loading-dots" />

        <template v-if="!companySearchLoading">
          <div
            v-if="companySearchResults.length > 0"
            class="tw:max-h-screen tw:overflow-y-auto">
            <p class="tw:mb-4">The following companies were found:</p>

            <div
              v-for="company in companySearchResults"
              :key="company.companyId">
              <div class="tw:mb-4 tw:flex tw:items-center tw:gap-2">
                <input
                  type="radio"
                  name="company"
                  class="tw:radio tw:border tw:border-solid tw:radio-sm tw:radio-primary"
                  :value="company.companyId"
                  @click="companySearchSelection = company.companyId" />
                <div>
                  <p class="tw:text-lg tw:font-bold">{{ company.name }}</p>
                  <div class="tw:flex tw:items-center tw:gap-1">
                    <span class="tw:text-sm tw:font-bold">{{ company.exchangeSymbol }}:</span>
                    <span class="tw:text-sm tw:font-bold">{{ company.tickerSymbol }}</span>
                    <span
                      class="tw:ml-2 tw:badge tw:text-xs tw:font-bold tw:uppercase"
                      :class="{
                        'tw:bg-company-green tw:text-white': company.tickerStatus.toLowerCase() === 'active',
                        'tw:border tw:badge-ghost tw:border-gray-300 tw:text-gray-600': company.tickerStatus !== 'Active',
                      }">
                      {{ company.tickerStatus }}
                    </span>
                  </div>
                  <div class="tw:text-sm tw:text-gray-500">{{ company.address }}</div>
                </div>
              </div>
            </div>
          </div>
          <div v-else>No results found.</div>
        </template>
      </template>
    </AppModal>
  </div>
</template>

<style scoped>
.progress {
  display: grid;
  align-content: center;
  align-items: center;
  justify-content: center;
  justify-items: center;
  height: 50vh;
}

.root {
  margin: auto auto 4rem auto;
  max-width: 1000px;
}

.side-by-side {
  display: grid;
  grid-template-columns: 0.4fr 0.4fr 0.2fr;
  grid-gap: 1rem;
}

fieldset {
  border: 0;
  border-bottom: 1px solid var(--v-border-base);
  display: grid;
  grid-template-columns: 0.382fr 0.618fr;
  grid-column-gap: 4rem;
  padding: 2rem 0 1rem;
}

fieldset > div:nth-child(1) > h2 {
  font-size: 1.3rem;
}

fieldset > div:nth-child(1) > p {
  color: var(--v-gray-base);
}

fieldset > div:nth-child(2) {
  padding-top: 0.5rem;
}

.buttons {
  text-align: right;
  padding-top: 2rem;
}

.company-item {
  border-bottom: 1px solid var(--v-border-base);
  width: 100%;
  padding: 0.5rem 0.25rem;
}

.stock-exchange-item-selection {
  color: black;
}
</style>
